import { ALL_GAME_NAMES, getGameSlug } from '../config/allGames';

const BROWSE_INTEREST_KEY = 'bazaar_featured_interest_games';
const MAX_BROWSE_SIGNALS = 30;

const RESERVED_FIRST_SEGMENTS = new Set([
  'ad',
  'admin',
  'moderator',
  'mod',
  'profile',
  'settings',
  'cart',
  'checkout',
  'messages',
  'auth',
  'sign-in',
  'login',
  'register',
  'seller',
  'orders',
  'boosting',
  'api',
  'webhook',
  'policy',
  'terms',
  'privacy',
  'about',
  'contact',
  'support',
  'search',
  'not-found',
  '404',
]);

/** Normalize game names for matching ad.game to interest signals (hyphen/spacing tolerant). */
export function normalizeGameKey(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

let slugToNameCache = null;

function getSlugToGameName() {
  if (slugToNameCache) return slugToNameCache;
  const map = new Map();
  for (const name of ALL_GAME_NAMES) {
    map.set(getGameSlug(name), name);
  }
  slugToNameCache = map;
  return map;
}

function persistBrowseSignals(entries) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(BROWSE_INTEREST_KEY, JSON.stringify(entries));
  } catch {
    // ignore quota / private mode
  }
}

/** Record interest in a game (e.g. from listing URL or ad detail). */
export function recordInterestGame(gameName) {
  if (!gameName || typeof gameName !== 'string') return;
  const trimmed = gameName.trim();
  if (!trimmed) return;

  if (typeof localStorage === 'undefined') return;

  try {
    const raw = localStorage.getItem(BROWSE_INTEREST_KEY);
    let list = [];
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    }
    const entry = { game: trimmed, ts: Date.now() };
    const filtered = list.filter((x) => x && x.game !== trimmed);
    const next = [entry, ...filtered].slice(0, MAX_BROWSE_SIGNALS);
    persistBrowseSignals(next);
  } catch {
    // ignore
  }
}

/** Call on route changes: first path segment as game slug → canonical name. */
export function recordInterestFromPathname(pathname) {
  if (!pathname || typeof pathname !== 'string') return;
  const parts = pathname.split('/').filter(Boolean);
  const first = parts[0];
  if (!first || RESERVED_FIRST_SEGMENTS.has(first)) return;

  const map = getSlugToGameName();
  const gameName = map.get(first);
  if (gameName) {
    recordInterestGame(gameName);
  }
}

/** Set of normalized keys (for ad.game matching). */
export function getInterestGameNameKeys() {
  const keys = new Set();

  if (typeof localStorage !== 'undefined') {
    try {
      const rawSearches = localStorage.getItem('recentSearches');
      if (rawSearches) {
        const arr = JSON.parse(rawSearches);
        if (Array.isArray(arr)) {
          for (const item of arr) {
            if (item && item.game) {
              const k = normalizeGameKey(item.game);
              if (k) keys.add(k);
            }
          }
        }
      }
    } catch {
      // ignore
    }

    try {
      const rawBrowse = localStorage.getItem(BROWSE_INTEREST_KEY);
      if (rawBrowse) {
        const arr = JSON.parse(rawBrowse);
        if (Array.isArray(arr)) {
          for (const item of arr) {
            if (item && item.game) {
              const k = normalizeGameKey(item.game);
              if (k) keys.add(k);
            }
          }
        }
      }
    } catch {
      // ignore
    }
  }

  return keys;
}

/**
 * Pick up to maxCount products: prefer games the user has shown interest in, shuffle within tiers.
 * If interestKeys is empty, selection is uniformly random from the pool.
 */
export function pickPersonalizedFeatured(products, maxCount, interestKeys) {
  if (!products?.length || !maxCount) return [];

  const cap = Math.min(maxCount, products.length);
  const hasInterest = interestKeys && interestKeys.size > 0;

  const scored = products.map((product) => {
    const key = normalizeGameKey(product.game);
    const interested = hasInterest && key && interestKeys.has(key);
    return {
      product,
      interested,
      roll: Math.random(),
    };
  });

  scored.sort((a, b) => {
    if (a.interested !== b.interested) {
      return a.interested ? -1 : 1;
    }
    return b.roll - a.roll;
  });

  const seen = new Set();
  const out = [];
  for (const row of scored) {
    const id = row.product.id;
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    out.push(row.product);
    if (out.length >= cap) break;
  }
  return out;
}
